import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { authApi, affiliatesApi } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';

interface AffiliateProfile {
  affiliate: {
    referralCode: string;
    status: string;
  };
  organization: {
    organizationId: string;
    name: string;
    slug: string;
  } | null;
}

export default function AffiliateSettings() {
  const { user, setUser } = useAuthStore();
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
  });
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [referralCode, setReferralCode] = useState('');
  const [affiliateProfile, setAffiliateProfile] = useState<AffiliateProfile | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Initialize profile from user data when user changes
  useEffect(() => {
    if (user) {
      // Use firstName/lastName if available, otherwise parse from name
      let firstName = user.firstName || '';
      let lastName = user.lastName || '';

      // If firstName/lastName not set but we have a name field, try to parse it
      if (!firstName && !lastName) {
        // Get name from the user object (might be stored as 'name')
        const fullName = (user as unknown as { name?: string }).name || '';
        if (fullName) {
          const nameParts = fullName.split(' ');
          firstName = nameParts[0] || '';
          lastName = nameParts.slice(1).join(' ') || '';
        }
      }

      setProfile({ firstName, lastName });
    }
  }, [user]);

  useEffect(() => {
    const fetchAffiliate = async () => {
      try {
        const response = await affiliatesApi.getMe();
        if (response.data.success) {
          setReferralCode(response.data.data.affiliate.referralCode);
          setAffiliateProfile(response.data.data);
        }
      } catch (error) {
        console.error('Failed to fetch affiliate:', error);
      }
    };
    fetchAffiliate();
  }, []);

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUpdating(true);

    try {
      const response = await authApi.updateMe(profile);
      if (response.data.success) {
        setUser(response.data.data.user);
        toast.success('Profile updated successfully');
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to update profile');
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (passwords.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setIsChangingPassword(true);

    try {
      const response = await authApi.changePassword(
        passwords.currentPassword,
        passwords.newPassword
      );
      if (response.data.success) {
        toast.success('Password changed successfully');
        setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleCopyReferralCode = () => {
    navigator.clipboard.writeText(referralCode);
    toast.success('Referral code copied!');
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-display font-bold text-charcoal">Settings</h2>
        <p className="text-charcoal/60 mt-1">Manage your account settings</p>
      </div>

      {/* Referral Code */}
      <div className="rounded-xl bg-warm-white shadow-sm">
        <div className="px-6 py-4 border-b border-tan/30">
          <h3 className="text-lg font-display font-semibold text-charcoal">
            {affiliateProfile?.organization
              ? `Your ${affiliateProfile.organization.name} Referral Code`
              : 'Your Referral Code'}
          </h3>
        </div>
        <div className="p-6">
          <div className="flex items-center gap-4">
            <code className="font-serif text-2xl text-rust bg-rust/10 px-4 py-2 rounded-lg">
              {referralCode || 'Loading...'}
            </code>
            <button
              onClick={handleCopyReferralCode}
              className="btn btn-secondary"
              disabled={!referralCode}
            >
              Copy
            </button>
          </div>
          <p className="text-charcoal/60 text-sm mt-4">
            {affiliateProfile?.organization
              ? `Share this code with customers of ${affiliateProfile.organization.name}. They can use it at checkout for attribution.`
              : 'Share this code with customers. They can use it at checkout for attribution.'}
          </p>
        </div>
      </div>

      {/* Profile */}
      <div className="rounded-xl bg-warm-white shadow-sm">
        <div className="px-6 py-4 border-b border-tan/30">
          <h3 className="text-lg font-display font-semibold text-charcoal">Profile</h3>
        </div>
        <form onSubmit={handleProfileUpdate} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-charcoal mb-1">First Name</label>
              <input
                type="text"
                value={profile.firstName}
                onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-charcoal mb-1">Last Name</label>
              <input
                type="text"
                value={profile.lastName}
                onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                className="input"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-charcoal mb-1">Email</label>
            <input
              type="email"
              value={user?.email || ''}
              className="input bg-cream/50"
              disabled
            />
            <p className="text-xs text-charcoal/50 mt-1">Email cannot be changed</p>
          </div>
          <button
            type="submit"
            disabled={isUpdating}
            className="btn btn-primary"
          >
            {isUpdating ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>

      {/* Change Password */}
      <div className="rounded-xl bg-warm-white shadow-sm">
        <div className="px-6 py-4 border-b border-tan/30">
          <h3 className="text-lg font-display font-semibold text-charcoal">Change Password</h3>
        </div>
        <form onSubmit={handlePasswordChange} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-charcoal mb-1">Current Password</label>
            <input
              type="password"
              value={passwords.currentPassword}
              onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })}
              className="input"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-charcoal mb-1">New Password</label>
            <input
              type="password"
              value={passwords.newPassword}
              onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
              className="input"
              required
              minLength={8}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-charcoal mb-1">Confirm New Password</label>
            <input
              type="password"
              value={passwords.confirmPassword}
              onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
              className="input"
              required
            />
          </div>
          <button
            type="submit"
            disabled={isChangingPassword}
            className="btn btn-primary"
          >
            {isChangingPassword ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
